/* eslint-disable react/prop-types */
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import { HTTP_STATUS } from "constants/HttpStatus";
import { HTTP_STATUS_MESSAGE } from "constants/HttpStatus";
import { leadColors } from "service/Common";
import Card from "@mui/material/Card";

function LeadActivites({ open, onClose, leadId }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const axiosPrivate = useAxiosPrivate();
  const [taskDetails, setTaskDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const getTaskDetails = (leadId) => {
    const option = {
      url: `om/get-tasks?leadId=${leadId}`,
      method: "get",
    };
    setLoading(true);
    axiosPrivate(option)
      .then((response) => {
        // console.log(response);
        if (
          response?.status === HTTP_STATUS.OK &&
          response?.data?.status === HTTP_STATUS_MESSAGE.OK
        ) {
          setTaskDetails(response?.data?.data);
        } else {
          setTaskDetails({});
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (leadId) {
      // console.log("Lead ID:", leadId);
      getTaskDetails(leadId);
    }
  }, [leadId]);

  const InfoRow = ({ label, value, color }) => (
    <MDBox display="flex" justifyContent="space-between" mt={0.5}>
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>

      {color ? (
        <MDTypography
          variant="caption"
          sx={{
            px: 0.8,
            py: 0.2,
            borderRadius: "6px",
            backgroundColor: `${color}.main`,
            color: "#fff",
            fontSize: "11px",
          }}
        >
          {value || "—"}
        </MDTypography>
      ) : (
        <MDTypography variant="caption" fontWeight="medium">
          {value || "—"}
        </MDTypography>
      )}
    </MDBox>
  );

  const statusColors = {
    Planned: "info",
    Positive: "success",
    Cancelled: "error",
  };

  const leadStateColors = {
    Cold: "#42a5f5",
    Warm: "#ffa726",
    Win: "#66bb6a",
  };

  // console.log(taskDetails);

  //   const TaskCard = ({ item }) => {
  //     return (
  //       <Card sx={{ p: 2, borderRadius: 2 }}>
  //         {/* HEADER */}
  //         <MDBox display="flex" justifyContent="space-between" mb={1}>
  //           <MDTypography variant="button" fontWeight="bold">
  //             {item?.taskType}
  //           </MDTypography>

  //           <MDTypography
  //             variant="caption"
  //             sx={{
  //               px: 1,
  //               py: 0.3,
  //               borderRadius: "6px",
  //               fontSize: "11px",
  //               backgroundColor: leadStateColors[item?.leadState] || "#9e9e9e",
  //               color: "#fff",
  //               textTransform: "uppercase",
  //             }}
  //           >
  //             {item?.leadState}
  //           </MDTypography>
  //         </MDBox>

  //         {/* LEAD INFO */}
  //         <MDTypography variant="caption" color="text">
  //           Lead
  //         </MDTypography>
  //         <MDTypography variant="body2" fontWeight="medium" mb={1}>
  //           {item?.lead_details?.leadName || "—"} ({item?.lead_details?.mobile1 || "—"})
  //         </MDTypography>

  //         {/* DETAILS */}
  //         <InfoRow label="Status" value={item?.status} color={statusColors[item?.status]} />
  //         <InfoRow
  //           label="Scheduled On"
  //           value={
  //             item?.taskScheduledOn
  //               ? dayjs.unix(item.taskScheduledOn).format("DD MMM YYYY, hh:mm A")
  //               : "—"
  //           }
  //         />
  //         <InfoRow label="Assigned To" value={item?.assigned_to} />
  //         <InfoRow label="Property" value={item?.propertyName || "—"} />

  //         {/* REMARK */}
  //         {(item?.completion_remark || item?.creation_remark) && (
  //           <MDBox mt={1}>
  //             <MDTypography variant="caption" color="text">
  //               Remark
  //             </MDTypography>
  //             <MDTypography variant="body2">
  //               {item?.completion_remark || item?.creation_remark}
  //             </MDTypography>
  //           </MDBox>
  //         )}
  //       </Card>
  //     );
  //   };

  const TaskCard = ({ item }) => {
    const [expand, setExpand] = useState(false);

    const isCompleted = [
      "Positive",
      "Negative",
      "Neutral",
      "Cancelled",
      "Interested",
      "Not_Interested",
    ].includes(item?.status);

    const statusColor = isCompleted ? "success" : "info";

    const dateValue = isCompleted ? item?.taskUpdatedOn : item?.taskScheduledOn;

    const remark = item?.completion_remark || item?.creation_remark;

    return (
      <Card
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: darkMode ? "#2c365f" : "#e0e0e0",
          backgroundColor: darkMode ? "#1f2937" : "#fff",
          transition: "0.2s",
          "&:hover": {
            boxShadow: darkMode ? "0 6px 16px rgba(0,0,0,0.6)" : "0 6px 16px rgba(0,0,0,0.08)",
          },
        }}
      >
        {/* HEADER */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="button" fontWeight="bold">
            {item?.taskType}
          </MDTypography>

          <MDTypography
            variant="h6"
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: "6px",
              fontSize: "11px",
              backgroundColor: leadStateColors[item?.leadState] || "#9e9e9e",
              color: "#fff",
              textTransform: "uppercase",
            }}
          >
            {item?.leadState}
          </MDTypography>
        </MDBox>

        {/* STATUS + DATE */}
        <MDBox display="flex" justifyContent="space-between" mt={1}>
          <MDTypography
            variant="h6"
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: "6px",
              fontSize: "11px",
              backgroundColor: `${statusColor}.main`,
              color: "#fff",
            }}
          >
            {item?.status}
          </MDTypography>

          <MDTypography variant="caption" fontWeight="medium">
            {dateValue ? dayjs.unix(dateValue).format("DD MMM YYYY, hh:mm A") : "—"}
          </MDTypography>
        </MDBox>

        {/* LEAD INFO */}
        <MDBox mt={1}>
          <MDTypography variant="caption" color="text">
            Lead
          </MDTypography>
          <MDTypography variant="body2" fontWeight="medium">
            {item?.lead_details?.leadName || "—"} • {item?.lead_details?.mobile1 || "—"}
          </MDTypography>
        </MDBox>

        {/* ASSIGNED */}
        <MDBox mt={0.5}>
          <MDTypography variant="caption">
            Assigned to: <strong>{item?.assigned_to || "—"}</strong>
          </MDTypography>
        </MDBox>

        {/* REMARK */}
        {remark && (
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text">
              Remark
            </MDTypography>

            <MDTypography variant="body2" sx={{ lineHeight: 1.4 }}>
              {expand || remark.length <= 120 ? remark : `${remark.substring(0, 120)}...`}
            </MDTypography>

            {remark.length > 120 && (
              <MDTypography
                variant="caption"
                sx={{
                  mt: 0.5,
                  color: "info.main",
                  cursor: "pointer",
                  fontWeight: 500,
                  display: "inline-block",
                }}
                onClick={() => setExpand(!expand)}
              >
                {expand ? "See Less" : "See More"}
              </MDTypography>
            )}
          </MDBox>
        )}
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#1a2035" : "#fff",
          color: darkMode ? "#fff" : "#000",
          borderRadius: 2,
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: darkMode ? "#fff" : "#000",
          backgroundColor: darkMode ? "#1f2937" : "#f5f5f5",
          borderBottom: `1px solid ${darkMode ? "#475894" : "#d9d9d9"}`,
        }}
      >
        <MDTypography variant="h5">Lead Activities</MDTypography>

        <IconButton onClick={onClose} sx={{ color: darkMode ? "#fff" : "#000" }}>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      {/* <DialogContent
        dividers
        sx={{
          backgroundColor: darkMode ? "#1a2035" : "#fff",
          "& .MuiTypography-root": {
            color: darkMode ? "#fff" : "#000",
          },
        }}
      >
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            {leadsDetails?.lead?.name || "—"}
          </MDTypography>

          <MDBox display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
            <MDTypography
              variant="caption"
              sx={{
                backgroundColor: leadColors?.[leadsDetails?.lead?.lead_state] || "#9e9e9e",
                color: "#fff",
                px: 1.2,
                py: 0.4,
                borderRadius: "6px",
                textTransform: "uppercase",
              }}
            >
              {leadsDetails?.lead?.lead_state}
            </MDTypography>

            <MDTypography variant="caption">
              Assigned to: {leadsDetails?.lead?.assigned_to || "—"}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant="h6" mb={1}>
            Contact Details
          </MDTypography>

          <MDBox display="grid" gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={1}>
            <InfoItem label="Name" value={leadsDetails?.lead?.name} />

            <InfoItem label="Mobile" value={leadsDetails?.lead?.mobile} />

            <InfoItem label="Alternate Mobile" value={leadsDetails?.lead?.mobile2} />

            <InfoItem label="Email" value={leadsDetails?.lead?.email} />

            <InfoItem label="Budget" value={leadsDetails?.lead?.budget} />

            <InfoItem label="Purpose" value={leadsDetails?.lead?.purpose} />

            <InfoItem label="Preferred Location" value={leadsDetails?.lead?.preferredLocation} />

            <InfoItem label="Preferred City" value={leadsDetails?.lead?.preferredCity} />

            <InfoItem label="Assigned To" value={leadsDetails?.lead?.assigned_to} />

            <InfoItem label="Lead Status" value={leadsDetails?.lead?.lead_state} badge />
          </MDBox>
        </MDBox>

        <MDBox>
          <MDTypography variant="h6" mb={2}>
            Call Logs
          </MDTypography>

          {leadsDetails?.callLogs && leadsDetails?.callLogs.length === 0 ? (
            <MDTypography variant="body2">No call logs found</MDTypography>
          ) : (
            <MDBox
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
              gap={2}
            >
              {leadsDetails?.callLogs &&
                leadsDetails?.callLogs.map((log) => <CallLogCard key={log._id} log={log} />)}
            </MDBox>
          )}
        </MDBox>
      </DialogContent> */}

      <DialogContent
        dividers
        sx={{
          backgroundColor: darkMode ? "#1a2035" : "#efededff",
          "& .MuiTypography-root": {
            color: darkMode ? "#fff" : "#000",
          },
        }}
      >
        {/* repeat(auto-fit, minmax(280px, 1fr)) */}
        <MDBox display="grid" gridTemplateColumns="1fr" gap={2}>
          {loading ? (
            <MDTypography variant="body2" color="text">
              Loading activities...
            </MDTypography>
          ) : taskDetails && taskDetails.length === 0 ? (
            <MDTypography variant="body2" color="text">
              No Activity Found for this lead.
            </MDTypography>
          ) : (
            taskDetails &&
            taskDetails.length != 0 &&
            taskDetails.map((item) => <TaskCard key={item.id} item={item} />)
          )}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

LeadActivites.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  leadId: PropTypes.any,
};

export default LeadActivites;
